import Store from '@ember-data/store';
import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { generateNameFields } from 'donor-elf-web/models/contact';
import AddSomethingModal, {
  type OpenArgs,
} from 'donor-elf-web/services/add-something-modal';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import Settings from 'donor-elf-web/services/settings';
import ContactValidation from 'donor-elf-web/validations/contact';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';

const customPropertyValidation = {
  v: validatePresence({ presence: true, message: "Can't be blank" }),
  l: validatePresence({ presence: true, message: "Can't be blank" }),
};

interface ComponentArgs {
  openArgs?: OpenArgs;
}

export default class AddSomethingModalContactIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);

    this.reset({
      recommendedByContactId:
        args.openArgs?.contactOptions?.recommendedByContactId,
    });
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare contactsCache: ContactsCache;
  @service declare router: RouterService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked changeset: any;
  dirty = false;
  @tracked addContactToJournal: boolean = false;
  @tracked showValidationErrors = false;
  @tracked hasErrors = false;
  @tracked saving = false;
  @tracked phoneChangesets: any[] = [];
  @tracked emailChangesets: any[] = [];
  @tracked groups: string[] = [];
  @tracked customPropertyChangesets: any[] = [];
  generatedNameFieldsManuallyChanged = false;
  @tracked gotoDetailPage = true;
  firstNameInputElement?: HTMLElement;

  reset(args: { recommendedByContactId?: string } = {}) {
    this.changeset = new Changeset(
      this.store.createRecord('contact', {
        active: true,
        recommendedByContactId: args.recommendedByContactId,
      }),
      lookupValidator(ContactValidation),
      ContactValidation,
    );
    this.dirty = false;
    this.addContactToJournal =
      this.addSomethingModal.showAddContactToJournalCheckbox;
    this.showValidationErrors = false;
    this.hasErrors = false;
    this.saving = false;
    this.phoneChangesets = [];
    this.emailChangesets = [];
    this.groups = [];
    this.customPropertyChangesets = [];
    this.generatedNameFieldsManuallyChanged = false;
    this.gotoDetailPage = this.addSomethingModal.showGotoDetailPage;
    if (this.firstNameInputElement) {
      setTimeout(() => {
        this.firstNameInputElement!.focus();
      });
    }
  }

  get metaKeyName() {
    return this.isMacComputer ? 'cmd' : 'ctrl';
  }

  get isMacComputer() {
    return window.navigator.platform.toUpperCase().indexOf('MAC') === 0;
  }

  get showSubscribeToNewsletterCheckbox() {
    return this.settings.fundIsSyncedWithUserNewsletterAccount;
  }

  get showAddContactToJournal() {
    return this.addSomethingModal.showAddContactToJournalCheckbox;
  }

  get showGotoDetailPage() {
    return this.addSomethingModal.showGotoDetailPage;
  }

  get allGroups() {
    return this.contactsCache.sortedGroups;
  }

  @action
  templateDidInsert() {
    document.addEventListener('keydown', this.handleDocumentKeydown);
  }

  @action
  templateWillDestroy() {
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  @action
  handleDocumentKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && !this.dirty) {
      this.addSomethingModal.close();
    }
  }

  @action
  handleInputEvent() {
    this.dirty = true;
  }

  @action
  handleKeydown(e: KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      if ((e.target as HTMLElement).dataset.individualNameField === 'true') {
        this.generateNameFields();
      }
      this.save({ addAnother: e.shiftKey });
    }
  }

  @action
  firstNameDidInsert(e: HTMLElement) {
    // record the firstName element so we can focus on it if the user wants to
    // add another contact after saving
    this.firstNameInputElement = e;
    setTimeout(() => {
      this.firstNameInputElement!.focus();
    });
  }

  @action
  generateNameFields() {
    if (!this.generatedNameFieldsManuallyChanged) {
      let names = generateNameFields({
        firstName: this.changeset.get('firstName'),
        lastName: this.changeset.get('lastName'),
        spouseFirstName: this.changeset.get('spouseFirstName'),
        spouseLastName: this.changeset.get('spouseLastName'),
      });
      this.changeset.set('name', names.name);
      this.changeset.set('mailingName', names.mailingName);
      this.changeset.set('greeting', names.greeting);
    }
  }

  @action
  handleGeneratedNameFieldManuallyChanged() {
    this.generatedNameFieldsManuallyChanged = true;
  }

  @action
  addPhone() {
    this.phoneChangesets = [
      ...this.phoneChangesets,
      new Changeset({ v: null, l: null }),
    ];
  }

  @action
  addEmail() {
    let syncWithMailchimp =
      this.settings.mailchimpFundSyncSlim?.automaticallySyncAllEmails ||
      (this.settings.mailchimpFundSyncSlim?.automaticallySyncFirstEmail &&
        this.emailChangesets.length === 0);

    this.emailChangesets = [
      ...this.emailChangesets,
      new Changeset({ v: null, l: null, syncWithMailchimp }),
    ];
  }

  @action
  saveGroups(selectedGroups: string[]) {
    this.groups = selectedGroups;
  }

  // commented out for now until we update the UI to work with all the custom property types
  // @action
  // addCustomProperty() {
  //   let changeset = new Changeset(
  //     { l: '', v: '' },
  //     lookupValidator(customPropertyValidation),
  //     customPropertyValidation
  //   );
  //   this.customPropertyChangesets = [
  //     ...this.customPropertyChangesets,
  //     changeset
  //   ]
  // }

  @action
  toggleAddContactToJournal() {
    this.addContactToJournal = !this.addContactToJournal;
  }

  @action
  toggleGotoDetailPage() {
    this.gotoDetailPage = !this.gotoDetailPage;
  }

  @action
  async save(args: { addAnother?: boolean } = {}) {
    this.saving = true;
    [
      'name',
      'mailingName',
      'firstName',
      'lastName',
      'spouseFirstName',
      'spouseLastName',
    ].forEach((nameProperty) => {
      this.changeset.set(
        nameProperty,
        this.changeset.get(nameProperty)?.trim(),
      );
    });
    this.changeset.set(
      'phones',
      this.convertValidPhoneOrEmailChangesetsToData(this.phoneChangesets),
    );
    this.changeset.set(
      'emails',
      this.convertValidPhoneOrEmailChangesetsToData(this.emailChangesets),
    );
    this.changeset.set(
      'emailAddressesToSyncWithMailchimp',
      this.emailChangesets
        .filter((x) => x.get('syncWithMailchimp'))
        .map((x) => x.get('v'))
        .filter((x) => isPresent(x)),
    );
    if (this.groups.length == 0) {
      this.changeset.set('groups', null);
    } else {
      this.changeset.set('groups', this.groups);
    }

    this.customPropertyChangesets = this.customPropertyChangesets.filter(
      (x) => isPresent(x.get('l')) || isPresent(x.get('v')),
    );
    await Promise.allSettled(
      this.customPropertyChangesets.map((x) => x.validate()),
    );
    await this.changeset.validate();
    if (
      this.changeset.get('isValid') &&
      this.customPropertyChangesets.every((x) => x.get('isValid'))
    ) {
      this.hasErrors = false;
      this.showValidationErrors = false;

      if (this.customPropertyChangesets.length == 0) {
        this.changeset.set('custom', null);
      } else {
        this.changeset.set('custom', {
          properties: this.customPropertyChangesets.map((x) => {
            return {
              l: x.get('l').trim(),
              v: x.get('v').trim(),
              t: 's',
            };
          }),
        });
      }

      let recommendedByContactId = this.changeset.get('recommendedByContactId');
      await this.changeset.save();
      let contact = this.changeset.get('data');
      contact.recommendedByContactId = undefined;
      this.contactsCache.addContact(contact);
      this.addSomethingModal.contactAdded({
        contact,
        goingToDetailPage: this.gotoDetailPage,
        addContactToJournal: this.addContactToJournal,
        recommendedByContactId: recommendedByContactId,
      });
      if (this.gotoDetailPage) {
        this.router.transitionTo('funds.fund.contacts.contact', contact.id);
      }
      if (args.addAnother) {
        this.reset();
      } else {
        this.addSomethingModal.close();
      }
    } else {
      this.hasErrors = true;
      this.showValidationErrors = true;
    }
    this.saving = false;
  }

  convertValidPhoneOrEmailChangesetsToData(changesets: any[]) {
    let validPhones = changesets.filter((x) => isPresent(x.get('v')));
    if (validPhones.length > 0) {
      return {
        data: validPhones.map((x) => {
          return {
            v: x.get('v').trim(),
            l: x.get('l')?.trim(),
          };
        }),
      };
    } else {
      return null;
    }
  }

  @action
  cancel() {
    this.addSomethingModal.close();
  }
}
